import { supabase } from '../../lib/supabase';

export const fmtMT = (v) => `${(Number(v) / 100000).toFixed(2)}L MT`;
export const fmtNum = (v) => Number(v).toLocaleString('en-IN');
export const fmtMonth = (d) => d ? new Date(d).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : '';
export const fmtMonthShort = (d) => d ? new Date(d).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }) : '';
export const pctChange = (curr, prev) => (prev !== null && prev !== undefined && Number(prev) !== 0)
  ? (((Number(curr) - Number(prev)) / Number(prev)) * 100)
  : null;
export const slugify = (s) => (s || '').toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
export const shiftYear = (dateStr, years) => {
  const d = new Date(dateStr);
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
};

export async function getLatestPeriod() {
  const { data, error } = await supabase
    .from('mi_v_national_monthly')
    .select('period_month')
    .order('period_month', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ? data.period_month : null;
}

// Fetches all rows in a period range via explicit pagination.
// Supabase/PostgREST caps unbounded queries at 1000 rows; ranges spanning several
// months x 621 mills can exceed that, silently truncating results otherwise.
export async function fetchAllPaginated(queryBuilderFn) {
  let rows = [];
  let from = 0;
  const PAGE_SIZE = 1000;
  while (true) {
    const { data, error } = await queryBuilderFn().range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    rows = rows.concat(data || []);
    if (!data || data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return rows;
}

// Indian Financial Year: April 1 - March 31 (used for the cumulative column, per KK's explicit call)
export function getFYStart(dateStr) {
  const d = new Date(dateStr);
  const year = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${year}-04-01`;
}
export function getFYLabel(dateStr) {
  const d = new Date(dateStr);
  const startYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `FY${String(startYear).slice(2)}-${String(startYear + 1).slice(2)}`;
}
// Plain-language version - avoids "FY" jargon per KK's explicit instruction
export function getFYPlainLabel(dateStr, t) {
  const d = new Date(dateStr);
  const startYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return t ? t('marketIntelligence.fyPlainLabel', 'Apr {{year}} onwards', { year: startYear }) : `Apr ${startYear} onwards`;
}

// Tops up an FY-cumulative total with quota from a fortnightly release (e.g. Sept
// 2026's 1st fortnight) that hasn't been folded into a single combined
// mi_release_orders row for its month yet. Safe to add into a cumulative because
// a cumulative is monotonic and additive - unlike surfacing a fortnight as
// "latest month" data, which would misrepresent a half-month as a full one.
//
// `afterPeriodMonth` is the caller's own latest complete month already present in
// mi_release_orders for the scope in question (national/mill/district - whatever
// the caller is summing for). A notice is included only if the CALENDAR MONTH its
// period_start falls in is strictly after that month - not if its raw
// period_start is later. That distinction matters once a month has two
// fortnights: fortnight 2's period_start (e.g. the 16th) is always later in the
// month than fortnight 1's (the 1st), so comparing raw period_start would keep
// including fortnight 2 forever, even after both fortnights are combined into
// one mi_release_orders row for that month - double-counting it. Truncating both
// sides to the 1st of the month first is what makes this the correct
// self-disabling guard: once a month's combined row exists, that month becomes
// the new latest month everywhere, every notice belonging to it (both fortnights)
// stops passing the filter together, and this stops contributing automatically -
// no flag to flip, no cleanup step to remember.
//
// Returns a Map from mill_id -> extra qty (MT). Callers key their own per-mill,
// per-district, or per-state aggregation off whichever mill_ids they already have
// in scope; this helper doesn't need to know which.
export async function fetchFortnightFYByMill(afterPeriodMonth) {
  if (!afterPeriodMonth) return new Map();
  const { data: notices, error: noticesErr } = await supabase
    .from('mi_fortnight_notices')
    .select('id, period_start');
  if (noticesErr) throw noticesErr;
  if (!notices || notices.length === 0) return new Map();

  const monthStart = (dateStr) => `${dateStr.slice(0, 7)}-01`;
  const relevantNotices = notices.filter((n) => monthStart(n.period_start) > afterPeriodMonth);
  if (relevantNotices.length === 0) return new Map();

  const [{ data: lines, error: linesErr }, { data: mills, error: millsErr }] = await Promise.all([
    supabase
      .from('mi_fortnight_release_lines')
      .select('mill_code_raw, allocated_qty_mt')
      .in('fortnight_notice_id', relevantNotices.map((n) => n.id)),
    supabase.from('mi_mills').select('id, mill_code'),
  ]);
  if (linesErr) throw linesErr;
  if (millsErr) throw millsErr;
  if (!lines || lines.length === 0) return new Map();

  const idByCode = new Map((mills || []).map((m) => [m.mill_code, m.id]));
  const byMill = new Map();
  lines.forEach((l) => {
    const millId = idByCode.get(l.mill_code_raw);
    if (!millId) return; // unresolvable code (e.g. a mill not yet in mi_mills) - can't attribute to any cumulative
    byMill.set(millId, (byMill.get(millId) || 0) + Number(l.allocated_qty_mt));
  });
  return byMill;
}

// Fetches ALL fortnight notices (active or not) whose calendar month is newer than
// `afterPeriodMonth` - i.e. not yet folded into a combined mi_release_orders row for
// that month. Same self-disabling monthStart(...) truncation guard as
// fetchFortnightFYByMill (reused verbatim, not reinvented): once a month's combined
// row lands, the caller's own afterPeriodMonth advances past it and that month's
// notices stop qualifying automatically - no flag to flip, no cleanup step.
// Unlike fetchFortnightFYByMill (which sums everything into one cumulative topup
// number), this keeps each notice separate and per-mill, so callers can render each
// still-gap-filling fortnight as its own labeled row (e.g. in a "month by month"
// table) instead of just topping up a total. Never throws.
export async function fetchGapFillingFortnightsByMill(afterPeriodMonth) {
  if (!afterPeriodMonth) return [];
  try {
    const { data: notices, error: noticesErr } = await supabase
      .from('mi_fortnight_notices')
      .select('id, period_start, period_end, period_label');
    if (noticesErr) throw noticesErr;
    if (!notices || notices.length === 0) return [];

    const monthStart = (dateStr) => `${dateStr.slice(0, 7)}-01`;
    const relevant = notices.filter((n) => monthStart(n.period_start) > afterPeriodMonth);
    if (relevant.length === 0) return [];

    const [{ data: lines, error: linesErr }, { data: mills, error: millsErr }] = await Promise.all([
      supabase
        .from('mi_fortnight_release_lines')
        .select('fortnight_notice_id, mill_code_raw, allocated_qty_mt')
        .in('fortnight_notice_id', relevant.map((n) => n.id)),
      supabase.from('mi_mills').select('id, mill_code'),
    ]);
    if (linesErr) throw linesErr;
    if (millsErr) throw millsErr;

    const idByCode = new Map((mills || []).map((m) => [m.mill_code, m.id]));
    const noticeById = new Map(relevant.map((n) => [n.id, n]));
    const byKey = new Map();
    (lines || []).forEach((l) => {
      const millId = idByCode.get(l.mill_code_raw);
      const notice = noticeById.get(l.fortnight_notice_id);
      if (!millId || !notice) return;
      const key = `${millId}::${notice.id}`;
      const existing = byKey.get(key);
      if (existing) { existing.qty += Number(l.allocated_qty_mt); return; }
      byKey.set(key, {
        millId,
        periodStart: notice.period_start,
        periodEnd: notice.period_end,
        periodLabel: notice.period_label,
        qty: Number(l.allocated_qty_mt),
      });
    });
    return [...byKey.values()].sort((a, b) => (a.periodStart < b.periodStart ? 1 : -1)); // newest first
  } catch (_e) {
    return [];
  }
}

// Pure merge, no fetch - combines a mill's mi_release_orders rows with its own
// gap-filling fortnights (already filtered to that mill, e.g. via
// fetchGapFillingFortnightsByMill's result) into one date-descending list. Each item
// is tagged with `kind` ('month' | 'fortnight') so callers never mislabel a
// half-month fortnight figure as if it were a full calendar month.
export function buildMergedPeriods(monthlyRows, gapFortnights) {
  const monthly = (monthlyRows || []).map((r) => ({
    kind: 'month',
    date: r.period_month,
    periodMonth: r.period_month,
    qty: Number(r.allocated_qty_mt),
  }));
  const fortnights = (gapFortnights || []).map((f) => ({
    kind: 'fortnight',
    date: f.periodStart,
    periodStart: f.periodStart,
    periodEnd: f.periodEnd,
    periodLabel: f.periodLabel,
    qty: f.qty,
  }));
  return [...monthly, ...fortnights].sort((a, b) => (a.date < b.date ? 1 : -1));
}

const FORTNIGHT_LOCALE_BY_LANGUAGE = {
  en: 'en-IN', hi: 'hi-IN', te: 'te-IN', ta: 'ta-IN', kn: 'kn-IN', mr: 'mr-IN', gu: 'gu-IN',
};

// Formats a period_start/period_end pair as a locale-aware range, e.g. "1 - 15 Sep 2026".
// Falls back gracefully (returns null) if either date is missing/unparseable.
export function formatFortnightRange(startStr, endStr, language) {
  if (!startStr || !endStr) return null;
  const start = new Date(startStr);
  const end = new Date(endStr);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const locale = FORTNIGHT_LOCALE_BY_LANGUAGE[language] || FORTNIGHT_LOCALE_BY_LANGUAGE.en;
  const dayFmt = new Intl.DateTimeFormat(locale, { day: 'numeric' });
  const endFmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  return `${dayFmt.format(start)} - ${endFmt.format(end)}`;
}

// Fetches the most recent ACTIVE fortnightly release notice (mi_fortnight_notices)
// together with its per-mill breakdown (mi_fortnight_release_lines joined to
// mi_mills via mill_code_raw = mill_code), for EXPLICIT display - e.g. a "Sep
// 1-15, 2026 (MT)" column/tile shown alongside Latest/FY-cumulative, not folded
// into them. This is deliberately separate from fetchFortnightFYByMill: that one
// guards against double-counting once a month is combined and can span multiple
// still-pending fortnights; this one always shows exactly the single most recent
// published fortnight, for transparency, regardless of combine state. Uses the
// same is_active flag as the FortnightNotice banner, so both appear/disappear
// together. Returns null if there's no active notice.
export async function fetchLatestFortnightBreakdown() {
  const { data: notice, error: noticeErr } = await supabase
    .from('mi_fortnight_notices')
    .select('id, period_start, period_end, period_label, notification_no, notification_date')
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (noticeErr) throw noticeErr;
  if (!notice) return null;

  const [{ data: lines, error: linesErr }, { data: mills, error: millsErr }] = await Promise.all([
    supabase.from('mi_fortnight_release_lines').select('mill_code_raw, allocated_qty_mt').eq('fortnight_notice_id', notice.id),
    supabase.from('mi_mills').select('id, mill_code'),
  ]);
  if (linesErr) throw linesErr;
  if (millsErr) throw millsErr;

  const idByCode = new Map((mills || []).map((m) => [m.mill_code, m.id]));
  const byMill = new Map();
  (lines || []).forEach((l) => {
    const millId = idByCode.get(l.mill_code_raw);
    if (!millId) return;
    byMill.set(millId, (byMill.get(millId) || 0) + Number(l.allocated_qty_mt));
  });

  return {
    periodStart: notice.period_start,
    periodEnd: notice.period_end,
    periodLabel: notice.period_label,
    notificationNo: notice.notification_no,
    notificationDate: notice.notification_date,
    byMill,
  };
}

// Detects a genuine recurring calendar-month pattern for a mill's history.
// Rule: same direction in >=75% of years observed, minimum 5 years of data.
export function detectSeasonalPattern(monthlyRows) {
  const byYearMonth = {};
  monthlyRows.forEach((r) => {
    const d = new Date(r.period_month);
    const y = d.getFullYear();
    const m = d.getMonth();
    byYearMonth[y] = byYearMonth[y] || {};
    byYearMonth[y][m] = Number(r.allocated_qty_mt);
  });
  const years = Object.keys(byYearMonth).map(Number).sort();
  if (years.length < 5) return null;

  let best = null;
  for (let m = 0; m < 11; m++) {
    let up = 0, down = 0, counted = 0;
    years.forEach((y) => {
      const a = byYearMonth[y]?.[m];
      const b = byYearMonth[y]?.[m + 1];
      if (a === undefined || b === undefined) return;
      counted++;
      if (a > b) up++;
      else if (b > a) down++;
    });
    if (counted < 5) continue;
    const majority = Math.max(up, down);
    const pct = majority / counted;
    if (pct >= 0.75 && (!best || pct > best.pct)) {
      best = { monthA: m, monthB: m + 1, direction: up > down ? 'AtoB' : 'BtoA', pct, years: counted };
    }
  }
  return best;
}

export const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export const RANK_DISCLOSURE = (monthLabel, t) =>
  t ? t('marketIntelligence.rankDisclosure', 'Ranks reflect {{month}} allocation only, computed by SugarHathi from published government release order data \u2014 not an official government ranking.', { month: monthLabel })
    : `Ranks reflect ${monthLabel} allocation only, computed by SugarHathi from published government release order data \u2014 not an official government ranking.`;

// Other mills in the same district, straight-line (Haversine) distance in km,
// farthest-first. Pure SQL function (get_district_mill_distances) — no external
// API, no caching needed, safe to call on every page load. Mills whose pincode
// has no lat/long match are already excluded server-side.
export async function fetchDistrictMillDistances(millId) {
  if (!millId) return { distances: [], error: null };
  const { data, error } = await supabase.rpc('get_district_mill_distances', { target_mill_id: millId });
  if (error) return { distances: [], error: error.message };
  return { distances: data || [], error: null };
}

